import type { UserImport } from '@src/containers/TalosAdmin/Users/ImportUserDialog';
import {
  Delete,
  Get,
  Patch,
  Post,
  Put,
  createCookie,
  getWsClient,
  isUserUpsertAllowed,
  logger,
  parseCookie,
  readCookie,
  stringifyCookie,
  type ISystem,
  type MarketAccount,
  type Organization,
  type OtherUser,
  type User,
} from '@talos/kyoko';
import { sortBy } from 'lodash-es';
import { envConfig } from './envConfig';

export const listCaptures = () => {
  return Get<string[]>(envConfig.VITE_AVA_API_ENDPOINT, `/admin/captures`);
};

export const listOrganizations = () => {
  return Get<Organization[]>(envConfig.VITE_AVA_API_ENDPOINT, `/admin/organizations`).then(orgs =>
    sortBy(orgs, 'Name')
  );
};

export const listSystems = () => {
  return Get<ISystem[]>(envConfig.VITE_AVA_API_ENDPOINT, `/admin/systems`);
};

export const listOrganizationsWithVM = () => {
  return listOrganizations().then(orgs => orgs.filter(o => o.Attrs && o.Attrs['HasOrgVm']));
};

interface CreateOrganizationParams {
  name: string;
  salesforceId: string;
  cloudProvider: string;
  zone: string;
  createMonitorUser: boolean;
  createPullRequest: boolean;
  whitelabelDomainName: string;
  orgCreationJiraRef: string;
  system?: ISystem;
}
export const createOrganization = ({
  name,
  salesforceId,
  cloudProvider,
  zone,
  createMonitorUser,
  createPullRequest,
  whitelabelDomainName,
  orgCreationJiraRef,
  system,
}: CreateOrganizationParams) => {
  type CreateOrganizationRequestParam = Omit<CreateOrganizationParams, 'system'> & {
    system?: string;
  };
  const payload: CreateOrganizationRequestParam = {
    name,
    salesforceId,
    cloudProvider,
    zone,
    createMonitorUser,
    createPullRequest,
    whitelabelDomainName,
    orgCreationJiraRef,
  };
  if (system) {
    payload['system'] = system.Name;
  }
  return Post(envConfig.VITE_AVA_API_ENDPOINT, `/admin/organizations`, payload);
};

export const updateOrganizationAttrs = ({ id, attrs }: { id: number; attrs: Record<string, unknown> }) => {
  return Patch(
    envConfig.VITE_AVA_API_ENDPOINT,
    `/admin/organizations/${id}/attrs`,
    {
      attrs: JSON.stringify(attrs),
    },
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }
  );
};

interface UserUpsert {
  Name: string;
  Email: string;
  ID: string;
  Roles?: string[];
}

export const deleteOrganization = (org: Organization) => {
  return Delete(envConfig.VITE_AVA_API_ENDPOINT, `/admin/organizations/${org.ID}`);
};

export const updateUserRoles = (user: UserUpsert) => {
  const { ok, message } = isUserUpsertAllowed(user);
  if (!ok) {
    return Promise.reject({ message });
  }

  return Put(envConfig.VITE_AVA_API_ENDPOINT, `/admin/users/${user.ID}/roles`, {
    roles: user.Roles,
  });
};

export const listUsersForAdmin = () => Get<OtherUser[]>(envConfig.VITE_AVA_API_ENDPOINT, `/admin/users`);

const userAdded = (user: User) =>
  Put(`${window.location.protocol}//${user.OrgApiEndpoint}/v1`, `/organization/users/${user.ID}`, {}).then(() => user);

export const inviteUser = (user: Omit<UserUpsert, 'ID'> & { OrgID: number }) => {
  const { ok, message } = isUserUpsertAllowed(user);
  if (!ok) {
    return Promise.reject({ message });
  }

  return Post(envConfig.VITE_AVA_API_ENDPOINT, `/admin/organizations/${user.OrgID}/users`, {
    name: user.Name,
    email: user.Email,
    roles: user.Roles,
  }).then(user => userAdded(user));
};
export const createUser = (user: UserImport) => {
  const { ok, message } = isUserUpsertAllowed({ Name: user.name, Email: user.email, Roles: user.roles });
  if (!ok) {
    return Promise.reject({ message });
  }

  return Put(envConfig.VITE_AVA_API_ENDPOINT, `/admin/organizations/${user.orgID}/users`, {
    auth0ID: user.auth0ID,
    name: user.name,
    roles: user.roles,
  }).then(user => userAdded(user));
};

export const deleteUser = (user: OtherUser) => {
  return Delete(envConfig.VITE_AVA_API_ENDPOINT, `/admin/organizations/x/users/${user.ID}`).then(() =>
    Delete(`${window.location.protocol}//${user.OrgApiEndpoint}/v1`, `/organization/users/${user.ID}`, {}).then(
      () => {}
    )
  );
};

export const morphTo = (user: OtherUser, reason: string | null = null) => {
  let path = `/admin/users/${user.ID}/morph`;
  if (reason != null) {
    path += `?reason=${encodeURIComponent(reason)}`;
  }
  return Put(envConfig.VITE_AVA_API_ENDPOINT, path).then(res => {
    const cookieValue = readCookie(envConfig.VITE_SESSION_COOKIE);
    const cookieData = parseCookie(cookieValue);
    if (cookieData == null) {
      logger.error(new Error('tried to morph to user without having a cookie set'));
    } else {
      cookieData.is_morphed = true;
      createCookie(envConfig.VITE_SESSION_COOKIE, stringifyCookie(cookieData), {
        expires: new Date(cookieData.expires_at * 1000),
        includeSubdomains: true,
      });
    }
    return res;
  });
};

export const unmorph = (): Promise<void> => {
  return Delete(envConfig.VITE_AVA_API_ENDPOINT, `/admin/users/morph`);
};

export const listMarketAccounts = (organization: Organization): Promise<MarketAccount[]> =>
  Get(`${window.location.protocol}//${organization.OrgApiEndpoint}`, `/v1/organization/accounts`);

export const createMarketAccount = (
  organization: Organization,
  marketAccount: {
    market: string;
    sourceAccountID: string;
    counterparty: string;
    referenceData: string;
    description: string;
  }
): Promise<MarketAccount> =>
  Post(`${window.location.protocol}//${organization.OrgApiEndpoint}`, `/v1/organization/accounts`, {
    market: marketAccount.market,
    sourceAccountID: marketAccount.sourceAccountID,
    counterparty: marketAccount.counterparty,
    referenceData: marketAccount.referenceData,
    description: marketAccount.description,
  });

export const deleteAppConfig = (userID: string | null) =>
  Delete(envConfig.VITE_AVA_API_ENDPOINT, `/admin/users/${userID}/app_config`);

export const forceLogout = (userID: string) => Delete(envConfig.VITE_AVA_API_ENDPOINT, `/admin/users/${userID}/logout`);

export const changeOrganization = (orgID: number | undefined) => {
  return Put(envConfig.VITE_AVA_API_ENDPOINT, `/organizations/${orgID}/use`).then(res => {
    getWsClient().unregisterAllSubscriptions();
    return res;
  });
};

export const createResetPasswordUrl = (userID: string) => {
  return Post(envConfig.VITE_AVA_API_ENDPOINT, `/admin/users/${userID}/change-password`);
};

export const sendResetPasswordEmail = (userID: string) => {
  return Post(envConfig.VITE_AVA_API_ENDPOINT, `/admin/users/${userID}/send-reset-password-email`);
};

export const searchUsersByDomain = (domain: string) =>
  Get(envConfig.VITE_AVA_API_ENDPOINT, `/admin/users?search-by-domain=${domain}`);

export const searchUsers = (search: string) =>
  Get<User[]>(envConfig.VITE_AVA_API_ENDPOINT, `/admin/users?search=${encodeURIComponent(search)}`);

export const moveUserToOrganization = (user: OtherUser, orgID: number, roles: string[]) => {
  const { ok, message } = isUserUpsertAllowed({ Name: user.Name, Email: user.Email, Roles: roles });
  if (!ok) {
    return Promise.reject({ message });
  }

  return Put(envConfig.VITE_AVA_API_ENDPOINT, `/admin/users/${user.ID}/organization`, {
    orgID: String(orgID),
    roles: roles.join(','),
  });
};
